// src/api/client.ts
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';

import { type ApiError, type ApiResponse } from '../schemas/auth.schema';
import { handleAuthExpiry } from '../utils/authExpiry';

import { getTokens, refreshAccessToken, clearTokens } from './auth';
import { API_CONFIG } from './config';

interface RetryRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

// Retry configuration
const retryConfig = {
  maxRetries: 3,
  retryDelay: (retryCount: number) => 2**retryCount * 1000, // Exponential backoff: 2s, 4s, 8s
  retryCondition: (error: AxiosError): boolean => 
    // Retry on network errors or 5xx errors (except 501 Not Implemented)
     (
      !error.response || // Network error
      (error.response.status >= 500 && error.response.status !== 501) // Server error except 501
    )
  ,
};

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a refresh token lock to prevent multiple refresh requests
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Add request interceptor for adding auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    const { accessToken, accessExpiry } = getTokens();

    if (accessToken && accessExpiry) {
      const expiryDate = new Date(accessExpiry);
      const now = new Date();

      if (expiryDate > now) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newAccessToken = await refreshAccessToken();
            onRefreshed(newAccessToken);
            config.headers.Authorization = `Bearer ${newAccessToken}`;
          } catch (error) {
            clearTokens();
            handleAuthExpiry('request-interceptor-refresh', error instanceof Error ? error : undefined);
          } finally {
            isRefreshing = false;
          }
        } else {
          await new Promise((resolve) => {
            subscribeTokenRefresh((token) => {
              config.headers.Authorization = `Bearer ${token}`;
              resolve(null);
            });
          });
        }
    }

    return config;
  },
  (error) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryRequestConfig;

    // If 401 error and not already retrying auth, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();

        // Retry the original request with the new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and handle auth expiry
        clearTokens();
        handleAuthExpiry('response-interceptor-refresh', refreshError instanceof Error ? refreshError : undefined);
        return Promise.reject(error);
      }
    }

    // Handle auto-retry for network errors and 5xx errors
    if (retryConfig.retryCondition(error)) {
      // Initialize retry count
      originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;

      // Check if we've reached the maximum number of retries
      if (originalRequest._retryCount <= retryConfig.maxRetries) {
        // Calculate delay based on retry count
        const delay = retryConfig.retryDelay(originalRequest._retryCount);

        // Retrying request

        // Wait for the specified delay
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry the request
        return axiosInstance(originalRequest);
      }
    }

    // If all retries have failed or if we shouldn't retry, reject with the error
    return Promise.reject(error);
  },
);

/**
 * Build a URL with query parameters
 */
export function buildUrl(
  baseUrl: string,
  params: Record<string, unknown> = {},
): string {
  const url = new URL(baseUrl, window.location.origin);

  // Add parameters to the URL
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      let stringValue: string;
      if (typeof value === 'object') {
        stringValue = JSON.stringify(value);
      } else if (typeof value === 'string') {
        stringValue = value;
      } else {
        stringValue = String(value);
      }
      url.searchParams.append(key, stringValue);
    }
  });

  // Return just the pathname + search to be combined with the API_CONFIG.BASE_URL
  return url.pathname + url.search;
}

/**
 * Generic API call function with error handling
 */
export async function apiCall<T = unknown, D = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await axiosInstance({
      method,
      url,
      data,
      ...config,
    });

    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // If it's a standard DRF error format
      const errorData = error.response.data;
      const statusCode = error.response.status;

      // Handle DRF standardized errors format
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const message =
          errorData.errors[0]?.detail ?? 'An unknown error occurred';
        const apiError: ApiError = {
          message,
          code: statusCode,
          details: errorData.errors,
        };
        return { success: false, error: apiError };
      }

      // Handle 404 as a special case
      if (statusCode === 404) {
        const apiError: ApiError = {
          message: errorData.detail ?? 'Not found',
          code: 404,
          details: errorData,
        };
        return { success: false, error: apiError };
      }

      return {
        success: false,
        error: {
          message: errorData.detail ?? 'An error occurred',
          code: statusCode,
          details: errorData,
        },
      };
    }

    // For network errors or other issues
    const apiError: ApiError = {
      message:
        error instanceof Error ? error.message : 'An unknown error occurred',
      type: 'network_error',
      details: 'network_error',
    };

    return { success: false, error: apiError };
  }
}

// Convenience methods
export const apiGet = <T>(url: string, config?: AxiosRequestConfig) =>
  apiCall<T>('GET', url, undefined, config);

export const apiPost = <T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
) => apiCall<T, D>('POST', url, data, config);

export const apiPatch = <T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
) => apiCall<T, D>('PATCH', url, data, config);

export const apiPut = <T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
) => apiCall<T, D>('PUT', url, data, config);

export const apiDelete = <T>(url: string, config?: AxiosRequestConfig) =>
  apiCall<T>('DELETE', url, undefined, config);
